<script setup>
import Button from '@/Components/Button.vue';
import { useForm } from '@inertiajs/vue3';
import { computed, ref } from 'vue';

const props = defineProps({
    buttonText: {
        type: String,
        default: 'Auto verkopen'
    },
    buttonColor: {
        type: String,
        default: 'primary' // 'primary' (orange) or 'secondary' (blue)
    },
    buttonTextSmall: {
        type: String,
        default: '' // Text to show only on small screens, if empty will use buttonText
    },
    formClasses: {
        type: String,
        default: 'flex flex-row items-center justify-center gap-4'
    },
    inputClasses: {
        type: String,
        default: 'w-full max-w-xs sm:w-72 md:w-80 h-12'
    },
    buttonClasses: {
        type: String,
        default: 'px-6'
    },
    errorClasses: {
        type: String,
        default: 'text-red-300 text-sm mt-3'
    },
    nlBlueBg: {
        type: Boolean,
        default: false
    },
    submitPath: {
        type: String,
        default: '/kenteken'
    }
});

const emit = defineEmits(['submit', 'success', 'error']);
const inputRef = ref(null); // Add ref for the input element

const form = useForm({
    license_plate: ''
});

// Expose the focus method to parent components
defineExpose({
    focus: () => {
        inputRef.value?.focus();
    }
});

function submit() {
    form.post(props.submitPath, {
        preserveScroll: 'errors',
        onSuccess: (response) => {
            //
        },
        onError: (errors) => {
            //
        }
    });
}

function formatLicensePlate(e) {
    form.clearErrors('license_plate');
    let licensePlate = e.target.value;

    let sidecode = GetSidecodeLicenseplate(licensePlate);
    if (sidecode) {
        form.license_plate = FormatLicenseplate(licensePlate, sidecode);
    }
}

function GetSidecodeLicenseplate(Licenseplate) {
    var arrSC = new Array;
    var scUitz = '';

    Licenseplate = Licenseplate.replace('-', '').toUpperCase();

    arrSC[0] = /^[a-zA-Z]{2}[\d]{2}[\d]{2}$/ // 1 XX-99-99
    arrSC[1] = /^[\d]{2}[\d]{2}[a-zA-Z]{2}$/ // 2 99-99-XX
    arrSC[2] = /^[\d]{2}[a-zA-Z]{2}[\d]{2}$/ // 3 99-XX-99
    arrSC[3] = /^[a-zA-Z]{2}[\d]{2}[a-zA-Z]{2}$/ // 4 XX-99-XX
    arrSC[4] = /^[a-zA-Z]{2}[a-zA-Z]{2}[\d]{2}$/ // 5 XX-XX-99
    arrSC[5] = /^[\d]{2}[a-zA-Z]{2}[a-zA-Z]{2}$/ // 6 99-XX-XX
    arrSC[6] = /^[\d]{2}[a-zA-Z]{3}[\d]{1}$/ // 7 99-XXX-9
    arrSC[7] = /^[\d]{1}[a-zA-Z]{3}[\d]{2}$/ // 8 9-XXX-99
    arrSC[8] = /^[a-zA-Z]{2}[\d]{3}[a-zA-Z]{1}$/ // 9 XX-999-X
    arrSC[9] = /^[a-zA-Z]{1}[\d]{3}[a-zA-Z]{2}$/ // 10 X-999-XX
    arrSC[10] = /^[a-zA-Z]{3}[\d]{2}[a-zA-Z]{1}$/ // 11 XXX-99-X
    arrSC[11] = /^[a-zA-Z]{1}[\d]{2}[a-zA-Z]{3}$/ // 12 X-99-XXX
    arrSC[12] = /^[\d]{1}[a-zA-Z]{2}[\d]{3}$/ // 13 9-XX-999
    arrSC[13] = /^[\d]{3}[a-zA-Z]{2}[\d]{1}$/ // 14 999-XX-9

    // Except licenseplates for diplomats
    scUitz = '^CD[ABFJNST][0-9]{1,3}$' // For example: CDB1 or CDJ45

    for (var i = 0; i < arrSC.length; i++) {
        if (Licenseplate.match(arrSC[i])) {
            return i + 1;
        }
    }
    if (Licenseplate.match(scUitz)) {
        return 'CD';
    }
    return false;
}

function FormatLicenseplate(Licenseplate, Sidecode) {
    Licenseplate = Licenseplate.replace('-', '').toUpperCase();

    if (Sidecode <= 6) {
        return Licenseplate.substr(0, 2) + '-' + Licenseplate.substr(2, 2) + '-' + Licenseplate.substr(4, 2)
    }
    if (Sidecode == 7 || Sidecode == 9) {
        return Licenseplate.substr(0, 2) + '-' + Licenseplate.substr(2, 3) + '-' + Licenseplate.substr(5, 1)
    }
    if (Sidecode == 8 || Sidecode == 10) {
        return Licenseplate.substr(0, 1) + '-' + Licenseplate.substr(1, 3) + '-' + Licenseplate.substr(4, 2)
    }
    if (Sidecode == 11 || Sidecode == 14) {
        return Licenseplate.substr(0, 3) + '-' + Licenseplate.substr(3, 2) + '-' + Licenseplate.substr(5, 1)
    }
    if (Sidecode == 12 || Sidecode == 13) {
        return Licenseplate.substr(0, 1) + '-' + Licenseplate.substr(1, 2) + '-' + Licenseplate.substr(3, 3)
    }
    return Licenseplate
}

const buttonDisplayText = computed(() => {
    if (props.buttonTextSmall && window.innerWidth < 640) {
        return props.buttonTextSmall;
    }
    return props.buttonText;
});
</script>

<template>
    <div v-auto-animate>
        <form @submit.prevent="submit" :class="formClasses">
            <div :class="['flex flex-row shadow-lg shadow-deepblue/15', inputClasses]">
                <div class="w-3/12 rounded-l-md flex items-center justify-center bg-royalblue-500">
                    <svg class="mx-auto" width="19" height="10" viewBox="0 0 19 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M9.47168 10H6.77832L2.43066 2.43945H2.36914C2.38737 2.75391 2.40332 3.07064 2.41699 3.38965C2.43066 3.70866 2.44434 4.02767 2.45801 4.34668C2.47168 4.66113 2.48535 4.97786 2.49902 5.29688V10H0.605469V0.00585938H3.27832L7.61914 7.49121H7.66699C7.65788 7.18132 7.64648 6.8737 7.63281 6.56836C7.61914 6.26302 7.60547 5.95768 7.5918 5.65234C7.58268 5.34701 7.57357 5.04167 7.56445 4.73633V0.00585938H9.47168V10ZM11.9941 10V0.00585938H14.1133V8.25H18.167V10H11.9941Z" fill="white" />
                    </svg>
                </div>
                <input 
                    ref="inputRef"
                    v-model="form.license_plate" 
                    placeholder="Jouw kenteken" 
                    @keyup="formatLicensePlate"
                    class="block w-full rounded-r-md text-center border-0 py-1.5 bg-yellow-400 text-gray-900 placeholder:text-gray-600 text-sm font-bold focus:ring-royalblue-500 focus:ring-2"
                />
            </div>
            
            <Button buttonType="submit" :color="buttonColor" :class="buttonClasses">
                <p class="text-lg font-medium">
                    <template v-if="buttonText.includes(' ')">
                        <span class="hidden sm:inline">{{ buttonText.split(' ')[0] }}</span>
                        <span :class="{ 'capitalize sm:normal-case': buttonText.includes(' ') }">
                            {{ buttonText.includes(' ') ? buttonText.split(' ')[1] : buttonText }}
                        </span>
                    </template>
                    <template v-else>
                        {{ buttonText }}
                    </template>
                </p>
            </Button>
        </form>
        
        <div v-if="form.errors.license_plate" :class="errorClasses">
            Er zijn geen gegevens gevonden voor het ingevulde kenteken {{ form.license_plate }}.
        </div>
    </div>
</template>
